import { z } from "zod";
import {
  AttachmentSchema,
  FileAttachmentSchema,
  VariableAttachmentSchema,
} from "./AttachmentSchema";
import { ToolSchema } from "../modules/tool-calling/schemas/ToolSchema";
import { providerAPIs } from "../modules/models/constants/providerAPIs";

// Prompt-flavoured aliases of the shared attachment schemas. Kept so existing
// `PromptAttachmentSchema` / `PromptFileAttachmentSchema` imports keep working;
// `./AttachmentSchema` is where the shapes are actually defined.
export const MessageFileVariableSchema = VariableAttachmentSchema;
export const PromptFileAttachmentSchema = FileAttachmentSchema;
export const PromptAttachmentSchema = AttachmentSchema;

const PromptMessageSchema = z.object({
  role: z.string(),
  content: z.string(),
  attachments: z.array(PromptAttachmentSchema).default([]),
});

const PromptVariableSchema = z
  .object({
    name: z.string().trim().nonempty(),
    type: z.enum(["string", "json", "number", "boolean"]).optional(),
    value: z.string().trim().optional(),
  })
  .or(
    z.object({
      name: z.string().trim().nonempty(),
      type: z.literal("attachment"),
      items: z.array(PromptFileAttachmentSchema).default([]),
    }),
  );

const PromptTestCaseSchema = z.object({
  id: z.string().uuid().optional(),
  name: z.string().trim().nonempty(),
  variables: z.array(PromptVariableSchema),
});

const PromptMetaSchema = z.object({
  model: z.string(),
  frequency_penalty: z.number(),
  max_completion_tokens: z.number().int().nullable().optional(),
  presence_penalty: z.number(),
  temperature: z.number().nullable().optional(),
  top_p: z.number().nullable().optional(),
  variables: z.array(PromptVariableSchema).optional(),
  messages: z.array(PromptMessageSchema),
  tools: z.array(ToolSchema).default([]),
  reasoning_effort: z.string().nullable().optional(),
  thinking_budget_tokens: z.number().int().nullable().optional(),
  top_k: z.number().int().nullable().optional(),
  verbosity: z.string().nullable().optional(),
  response_format: z.string().default("text"),
  structured_output: z
    .object({ schema: z.string(), name: z.string().optional() })
    .nullable()
    .optional(),
  stream: z.boolean(),
  provider_api: z.string().default(providerAPIs.CHAT_COMPLETIONS),
  display_mode: z.enum(["basic", "pro"]).default("basic"),
});

const PromptSchema = z.object({
  id: z.string(),
  type: z.literal("prompt"),
  name: z.string().trim().nonempty(),
  icon: z.string(),
  parent_id: z.string().nullable(),
  version_id: z.string(),
  version_name: z.string(),
  version_description: z.string(),
  meta: PromptMetaSchema,
  test_cases: z.array(PromptTestCaseSchema).optional(),
  role: z.enum(["owner", "editor", "viewer"]),
  sharing_status: z.enum(["public", "private", "shared"]),
  created_at: z.string(),
  updated_at: z.string(),
});

export {
  PromptMessageSchema,
  PromptMetaSchema,
  PromptSchema,
  PromptTestCaseSchema,
  PromptVariableSchema,
};
